import routes from "./routes";
import {
  createRouter,
  createWebHistory,
  type NavigationFailure,
  type RouteLocationNormalized,
  type RouteLocationRaw,
} from "vue-router";
import { useUser } from "@/store/user";
import { usePagination } from "@/store/pagination";
import { useSettings } from "@/store/settings";
import type { VariantFeatures } from "@/variants";
import { intersection } from "lodash";
import { useConfig } from "@/store/config";
import { useFeatureFlags } from "@/composables/useFeatureFlags";
import type { FeatureKey } from "@/config/features";
import { areUtilityPagesEnabled } from "@/utilities/functions/environments";
import { savePostLoginRedirect } from "@/utils/postLoginRedirect";

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.afterEach((to, from) => {
  const configStore = useConfig();
  const paginationStore = usePagination();
  if (to.name !== from.name) {
    paginationStore.resetPagination();
  }

  if (configStore?.meta?.name) {
    document.title = configStore.meta.name;
    return;
  }
  // GAIA-1972: the local dev host (development.local.*) and every gaia.* host showed the
  // "HD Admin" title; they are GAIA too.
  const hostname =
    typeof window !== "undefined" ? window.location.hostname ?? "" : "";
  if (window.location.href?.includes("localhost")) {
    document.title = "localhost";
  } else if (
    hostname.includes("development.local") ||
    hostname.includes("gaia.")
  ) {
    document.title = "GAIA";
  } else {
    document.title = "HD Admin";
  }
});

router.beforeEach(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
  ): Promise<void | RouteLocationRaw | NavigationFailure> => {
    const userStore = useUser();

    // Feature-flag gate. We walk `to.matched` (not just `to.meta`) so a
    // parent route with `meta.featureFlag` covers all its children without
    // each child needing to repeat the flag. First disabled flag wins —
    // users get bounced to home rather than seeing a half-rendered page.
    const gatedFlag = to.matched
      .map((record) => record.meta?.featureFlag as FeatureKey | undefined)
      .find((flag): flag is FeatureKey => !!flag);
    if (gatedFlag) {
      const { isEnabled } = useFeatureFlags();
      if (!isEnabled(gatedFlag)) {
        return { name: "home" };
      }
    }

    /**
     * Pagine di sola utilità (/placeholder, /dev, test MFE): chiuse dove
     * VITE_UTILITY_PAGES_ENABLED non è true. Il controllo vive qui e non solo
     * nella costruzione del menu, altrimenti la pagina resterebbe raggiungibile
     * digitando l'URL.
     */
    if (to.meta.utilityPage && !areUtilityPagesEnabled()) {
      console.log(
        `User tried to access a utility-only route`,
        to.path,
        ` which is disabled in this environment,`,
        ` redirecting.`
      );
      return { name: "home" };
    }

    if (userStore.isSuspended) {
      if (to.meta.authRequired) {
        console.log("Suspended-user");
        return;
      }
    }

    if (userStore.isLoggedIn) {
      if (userStore.getSavedRole?.status == "Pending" && to.name !== "wrong-role") {
        if (userStore.getSavedRoleName != "" && userStore.getSavedRolePage) {
          console.warn(userStore.getSavedRoleName + " pending: -> " + userStore.getSavedRolePage);
          if (window.location.hostname != "development.local.harleydikkinson.com") {
            if (window.location.pathname != "/account/" + userStore.getSavedRolePage) {
              window.location.href = "/account/" + userStore.getSavedRolePage;
              return;
            }
          }
        }

        return {
          name: "wrong-role",
        };
      }
    }

    // Redirect the user to its previous location if any
    // or to home on routes that are meant for guests only
    if (to.meta.guestOnly) {
      if (userStore.isLoggedIn) {
        console.log("tried to access a guest only route while logged in");
        if (to.query.redirect) {
          console.log("redirecting as specified");
          const { name, redirect, ...rest } = to.query;
          return {
            name: name?.toString(),
            query: rest,
          };
        } else {
          console.log("redirecting to home");
          return {
            name: "home",
          };
        }
      }
    }

    if (to.meta.authRequired) {
      if (!userStore.isLoggedIn) {
        // Ricorda la destinazione: Welcome la riprende dopo il login Keycloak
        savePostLoginRedirect(to.fullPath);
        return { name: "welcome" };
      } else {
        if (userStore.missingAgreements.length > 0 && to.name !== "additional-agreement") {
          return {
            name: "additional-agreement",
          };
        }

        if (!userStore.hasAccessToApplication && to.name !== "wrong-role") {
          return {
            name: "wrong-role",
          };
        }
      }
      /**
       * When routes are bound by role ensure the user has at least one of the allowed roles
       */
      if (to.meta.privilege) {
        const privilege: { resource: string; attribute: string } | null =
          to.meta?.privilege ?? null;
        if (privilege && userStore.isHidden(privilege.attribute, privilege.resource)) {
          console.log(
            `User tried to access a route`,
            to.path,
            ` which he is not authorized to enter,`,
            ` redirecting.`
          );
          return router.push({ name: "home" });
        }
      }
      if (to.meta.roles) {
        const matching = intersection(
          userStore.roles.map((r) => r.roleName),
          to.meta.roles
        );
        if (!matching.length) {
          console.log(
            `User tried to access a route`,
            to.path,
            ` which he is not authorized to enter,`,
            ` redirecting.`
          );
          return router.push({ name: "home" });
        }
      }
      /**
       * Route legate a una capability di variante (variant registry):
       * nascoste quando il flag della variante corrente è false.
       */
      if (to.meta.variantFeature) {
        const settingsStore = useSettings();
        const feature = to.meta.variantFeature as keyof VariantFeatures;
        if (settingsStore.effectiveVariantConfig.features[feature] === false) {
          console.log(
            `User tried to access a route`,
            to.path,
            ` which is disabled for this application variant,`,
            ` redirecting.`
          );
          return router.push({ name: "home" });
        }
      }
    }
  }
);

export default router;
